import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { finalizePaymentInvoice } from '@/api/payments';
import { queryClient } from '@/common/react-query';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY } from '@/config';
import { PaymentTransactionContext, useTransactionContext } from '@/context/transaction';
import { stripePromise } from '@/utils';

export type PaymentConfirmationProps = {
    transactionTitleName?: string;
    successMessage?: string;
    paymentProcessingMessage?: string;
    children?: ReactNode;
    open: boolean;
    onClose: (success: boolean) => void;
    onSuccess?: () => void;
};

export const PaymentConfirmation = ({ open, ...props }: PaymentConfirmationProps) => {
    const transactionContext = useTransactionContext('payment');

    if (transactionContext.isPending || !open) {
        return false;
    }

    return <PaymentConfirmationModal {...props} context={transactionContext} />;
};

type PaymentConfirmationModalProps = Omit<PaymentConfirmationProps, 'open'> & {
    context: PaymentTransactionContext;
};

const PaymentConfirmationModal = ({
    context,
    onClose,
    onSuccess,
    transactionTitleName = 'Transaction',
    successMessage,
    paymentProcessingMessage,
    children,
}: PaymentConfirmationModalProps) => {
    const navigate = useNavigate();
    const {
        invoice: { id: invoiceId },
        paymentMethod,
        backTarget,
        notFromAccountBalance,
    } = context;
    /*
     * We're working on finalizing and confirming the payment intent.
     */
    const [isProcessing, setProcessing] = useState(true);
    /*
     * We attempted the charge but Stripe is still processing (probably a delayed ACH)
     */
    const [isPaymentProcessing, setPaymentProcessing] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const [error, setError] = useState<string>();
    const [receiptHref, setReceiptHref] = useState<string>();
    const title = isProcessing
        ? `${transactionTitleName} processing...`
        : isSuccess
          ? `${transactionTitleName} successful`
          : isPaymentProcessing
            ? `${transactionTitleName} initiated`
            : `${transactionTitleName} unsuccessful`;

    const handleSuccess = useCallback(() => {
        setSuccess(true);
        queryClient.invalidateQueries({ queryKey: ['stripe-invoices-list'] });
        if (!notFromAccountBalance) {
            // if this wasn't drawn from account balance, no need to invalidate immediately (although if this is an account balance refill,
            // our consumer will handle temporarily updating the cache and then a delayed refresh in onSuccess)
            queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY] });
        }
        onSuccess?.();
    }, [onSuccess, notFromAccountBalance]);

    useEffect(() => {
        setProcessing(true);
        setSuccess(false);
        setPaymentProcessing(false);
        setError(undefined);
        finalizePaymentInvoice(invoiceId, { notFromAccountBalance })
            .then(async (response) => {
                const stripe = await stripePromise;
                const invoice = response.data.invoice;
                const clientSecret = invoice?.confirmation_secret?.client_secret;
                if (invoice.hosted_invoice_url) {
                    setReceiptHref(invoice.hosted_invoice_url);
                }

                if (invoice.status === 'paid') {
                    // no charge, completed immediately
                    handleSuccess();
                    return;
                }

                if (!stripe || !clientSecret) {
                    throw new Error(
                        `Unable to confirm payment on invoice ${invoiceId} without Stripe and client secret`
                    );
                }

                const paymentResult = await stripe.confirmPayment({
                    clientSecret,
                    redirect: 'if_required',
                    confirmParams: {
                        payment_method: paymentMethod,
                    },
                });

                let paymentIntent = paymentResult.paymentIntent;

                if (paymentResult.error) {
                    if (paymentResult.error.payment_intent?.status.startsWith('requires_')) {
                        // have observed requires_payment_method coming in as an error, but the paymentResult.error.message is generic and useless,
                        // so our normal handling of them is better, below
                        paymentIntent = paymentResult.error.payment_intent;
                    } else {
                        throw paymentResult.error;
                    }
                }

                if (paymentIntent?.status) {
                    switch (paymentIntent?.status) {
                        case 'succeeded':
                            handleSuccess();
                            break;
                        case 'processing':
                            setPaymentProcessing(true);
                            break;
                        case 'requires_payment_method':
                            setError(
                                'There was an error processing your payment. Please try again with a different payment method.'
                            );
                            break;
                        case 'requires_action':
                            if (paymentIntent.next_action?.type === 'redirect_to_url') {
                                // This _should_ get handled automatically by the SDK, but just in case...
                                navigate(paymentIntent.next_action.redirect_to_url!.url!);
                            } else {
                                setError(`Unexpected required next action: ${paymentIntent.next_action?.type}`);
                            }
                            break;
                        default:
                            setError(`Unexpected payment status: ${paymentIntent.status}`);
                    }
                } else {
                    setError('Unexpected response while attempting to confirm payment');
                }
            })
            .catch((e) => {
                if ('message' in e) {
                    setError(e.message);
                } else {
                    setError(String(e));
                }
            })
            .finally(() => setProcessing(false));
    }, [paymentMethod, invoiceId, navigate, handleSuccess, notFromAccountBalance]);

    return (
        <Dialog open={true} onClose={() => {}} aria-labelledby="payment-dialog-title" maxWidth="md">
            <DialogTitle id="payment-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {isProcessing && <GenericSkeleton lines={2} />}
                {error && <Alert severity="error">{error}</Alert>}
                {isSuccess && (
                    <Typography>{successMessage ?? 'Your transaction was completed successfully!'}</Typography>
                )}
                {isPaymentProcessing && (
                    <Typography>
                        {paymentProcessingMessage ??
                            'Your transaction is still processing. Your purchase will be credited once the payment is complete, which may take a few days.'}
                    </Typography>
                )}
                {!isProcessing && !error && children}
            </DialogContent>
            {!isProcessing && (
                <DialogActions>
                    {receiptHref && (
                        <Button href={receiptHref} target="_blank" variant="text">
                            View receipt
                        </Button>
                    )}
                    {(isSuccess || isPaymentProcessing) && (
                        <Button
                            onClick={() => navigate(backTarget?.href ?? '/')}
                            autoFocus
                            variant="contained"
                            data-testid="payment-confirm-done-button"
                        >
                            Done
                        </Button>
                    )}
                    {error && (
                        <Button
                            onClick={() => onClose(false)}
                            autoFocus
                            variant="contained"
                            data-testid="payment-confirm-close-button"
                        >
                            Back
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};
